<template>
  <div
    class="full-width q-my-md"
  >
    <div class="header-container notify-warning flex justify-between items-center text-left">
      <span>
        {{ $t('sessions.noteTemplate.changeRequest.bannerTitle') }}
      </span>
      <q-btn
        v-if="canDeleteRequest"
        flat
        icon="delete"
        class="q-pa-none"
        @click="deleteChangeRequest"
      >
        <q-tooltip>{{ $t('sessions.noteTemplate.changeRequest.bannerDeleteBtn') }}</q-tooltip>
      </q-btn>
    </div>
    <q-card
      bordered
      flat
      square
      class="flex row justify-between items-end card"
    >
      <q-card-section>
        <q-list>
          <q-item
            v-for="changedItem in changedItems"
            :key="changedItem.key"
            dense
            class="q-pa-none"
          >
            <q-item-section>
              <div class="row q-col-gutter-md">
                <span class="text-bold">{{ changedItem.label }} </span>
                <span class="text-strike">{{ changedItem.prevValue }}</span>
                <q-icon
                  name="arrow_right_alt"
                  size="xs"
                />
                <span>{{ changedItem.newValue }}</span>
              </div>
            </q-item-section>
          </q-item>
        </q-list>
        <div class="text-left q-pt-sm">
          <span class="text-bold q-mr-md">Reason</span>
          <span>{{ reason }}</span>
        </div>
      </q-card-section>
      <q-card-section
        v-if="showRequestRejectedBanner"
        class="full-width flex justify-between column q-my-md bg-red-1 text-left reject-banner"
      >
        <div class="flex justify-between items-center">
          <span class="text-uppercase text-bold reject-banner-text">
            {{ $t('sessions.sessionCard.changeRequestBanner.requestRejected') }}
          </span>
          <q-btn
            v-if="userHasPrivilege('sessionActivities/allowApproveChangeRequests')"
            flat
            icon="delete"
            class="q-pa-none"
            @click="deleteReject"
          />
        </div>
        <div class="text-left q-pt-sm">
          <span class="text-bold q-mr-md ">Reason</span>
          <span>{{ originalSessionActivity.changeRequest?.rejectionReason }}</span>
        </div>
      </q-card-section>
      <q-card-actions
        v-if="showActionButtons"
        class="justify-end q-px-md"
      >
        <q-btn
          flat
          color="red"
          :label="$t('common.buttonReject')"
          @click="rejectChangeRequest"
        />
        <q-btn
          flat
          color="primary"
          :label="$t('common.buttonApprove')"
          @click="approveChangeRequest"
        />
      </q-card-actions>
      <q-card-actions
        v-if="showSubmitNewRequestButton"
        class="justify-end q-px-md q-pt-none q-pb-md full-width"
      >
        <q-btn
          flat
          color="primary"
          :label="$t('sessions.sessionCard.changeRequestBanner.newRequestBtn')"
          @click="createChangeRequest"
        />
      </q-card-actions>
    </q-card>
  </div>
</template>

<script>
import { Timestamp } from 'firebase/firestore';
import { isFunction } from 'lodash';
import { mapState } from 'pinia';
import { useTime } from 'src/composables/time';
import AppointmentsMixin from 'src/mixins/AppointmentsMixin';
import Auth from 'src/mixins/Auth';
import Interactions from 'src/mixins/Interactions';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';

export default {
  name: 'ChangeRequestBanner',

  mixins: [AppointmentsMixin, Toolbox, Auth, Interactions],

  props: {
    originalSessionActivity: {
      type: Object,
      required: true,
    },
    requestedChanges: {
      type: Object,
      required: true,
    },
    reason: {
      type: String,
      default: '',
    },
    canDeleteRequest: {
      type: Boolean,
      default: false,
    },
    isInSignatureModal: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['delete-request', 'reject-request', 'approve-request', 'delete-reject', 'create-request'],

  setup() {
    const { getLocaleTimeString, isSameDate } = useTime();
    return { getLocaleTimeString, isSameDate };
  },

  data() {
    return {
      changeFormatting: {
        startTimestamp: {
          label: this.$t('sessions.modals.sessionActivity.labelStartTime'),
          format: (startTimestamp) => {
            if (!this.isStartDateChanged) {
              return this.getLocaleTimeString(startTimestamp, { options: { hour: 'numeric', minute: '2-digit' } });
            }
            return startTimestamp instanceof Timestamp
              ? this.getLocaleTimeString(startTimestamp, { options: { hour: 'numeric', minute: '2-digit' } })
              : '';
          },
        },
        endTimestamp: {
          label: this.$t('sessions.modals.sessionActivity.labelEndTime'),
          format: (endTimestamp) => {
            if (!this.isEndDateChanged) {
              return this.getLocaleTimeString(endTimestamp, { options: { hour: 'numeric', minute: '2-digit' } });
            }
            return endTimestamp instanceof Timestamp
              ? this.getLocaleTimeString(endTimestamp, { options: { hour: 'numeric', minute: '2-digit' } })
              : '';
          },
        },
      },
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'isParent']),

    changedItems() {
      const showChangeKeys = Object.keys(this.changeFormatting);

      const requestedChangeKeys = Object.keys(this.requestedChanges).filter((requestedChangeKey) => {
        return showChangeKeys.includes(requestedChangeKey);
      });
      return requestedChangeKeys.map((requestedChangeKey) => {
        return {
          key: requestedChangeKey,
          label: this.changeFormatting[requestedChangeKey].label,
          prevValue: this.getFormattedValue(this.originalSessionActivity, requestedChangeKey),
          newValue: this.getFormattedValue(this.requestedChanges, requestedChangeKey),
        };
      });
    },

    isStartDateChanged() {
      const prevStartTimestamp = this.originalSessionActivity.startTimestamp;
      const newStartTimestamp = this.requestedChanges.startTimestamp;

      return newStartTimestamp instanceof Timestamp
        && prevStartTimestamp instanceof Timestamp
        && !this.isSameDate(newStartTimestamp.toDate(), prevStartTimestamp.toDate(), 'minute');
    },

    isEndDateChanged() {
      const prevEndTimestamp = this.originalSessionActivity.endTimestamp;
      const newEndTimestamp = this.requestedChanges.endTimestamp;

      return newEndTimestamp instanceof Timestamp
        && prevEndTimestamp instanceof Timestamp
        && !this.isSameDate(newEndTimestamp.toDate(), prevEndTimestamp.toDate(), 'minute');
    },

    showRequestRejectedBanner() {
      return this.originalSessionActivity.changeRequest?.status === 'rejected'
        && !this.isInSignatureModal
        && !this.isParent;
    },

    showActionButtons() {
      return this.userHasPrivilege('sessionActivities/allowApproveChangeRequests')
        && this.originalSessionActivity.changeRequest?.status === 'pending'
        && !this.isInSignatureModal;
    },

    showSubmitNewRequestButton() {
      return this.originalSessionActivity.changeRequest?.status === 'rejected'
        && this.userDetails.userId === this.originalSessionActivity.changeRequest?.user
        && !this.isInSignatureModal;
    },
  },

  methods: {
    getFormattedValue(obj, requestedChangeKey) {
      const formatFunc = this.changeFormatting[requestedChangeKey].format;
      if (isFunction(formatFunc)) {
        return formatFunc(obj[requestedChangeKey], obj) || this.$t('common.placeholderEmpty');
      }
      return obj[requestedChangeKey] || this.$t('common.placeholderEmpty');
    }, // getFormattedValue()

    deleteChangeRequest() {
      this.$emit('delete-request');
    }, // deleteChangeRequest()

    rejectChangeRequest() {
      this.$emit('reject-request');
    }, // rejectChangeRequest()

    approveChangeRequest() {
      this.$emit('approve-request');
    }, // approveChangeRequest()

    deleteReject() {
      this.$emit('delete-reject');
    }, // deleteReject()

    createChangeRequest() {
      this.$emit('create-request');
    }, // createChangeRequest
  },
};
</script>

<style lang="scss" scoped>
.header-container {
  padding: 16px;
  border: 1px solid rgba(0, 0, 0, 0.25);
  border-radius: 4px 4px 0 0;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.25rem;
}

.card{
  border-bottom-left-radius: 4px !important;
  border-bottom-right-radius: 4px !important;
  border: 1px solid rgba(0, 0, 0, 0.25);
  border-top: none;
}

.reject-banner{
  border-radius: 4px !important;
  border: 1px solid #EE4035 !important;
  margin-left: 16px !important;
  margin-right: 16px !important;
}

.reject-banner-text {
  color: #EE4035;
}
</style>
